import { useState, useMemo, useEffect } from 'react';
import { isSameDay } from 'date-fns';
import { MenuItem, InlineSelect, Tooltip, Typography, WithInfoIcon, KeyboardDatePicker } from 'seagull';
import { useIoContext } from "../../../contexts";
import { useOnMount } from "../../../hooks/useOnMount";
import { serviceCostModel, serviceModel } from "../../../models";
import { DiscountPeriodExpirationModel } from "../../../models/DiscountPeriodExpirationModel";
import { isAwaitingApproval, validateDiscountPeriodExpiration } from "../../../models/service";
import pluralize from "../../../utils/pluralize";
import { AdvancedSettingsControl } from "../AdvancedSettingsControl/AdvancedSettingsControl";
import { AdvancedSetting } from "../ServiceAdvancedSettings.types";
import { getInitialDiscountPeriodExpirationState } from "../ServiceAdvancedSettings.utils";
import styles from "./DiscountPeriodControl.module.scss";
import { getAmountOptions, getDateRange, PERIOD_OPTIONS } from "./DiscountPeriodControl.utils";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export function DiscountPeriodControl(props) {
  const {
    service,
    info,
    label,
    isReadOnly,
    disabled,
    testId: testPrefix,
    disabledTooltip,
    onDiscountPeriodExpirationChange,
    relationshipState,
    onToggle
  } = props;
  const {
    getNow
  } = useIoContext();
  const now = useMemo(() => getNow(), []);
  const isRelationshipApproved = relationshipState.type === 'approved';
  const effectiveDate = relationshipState.effectiveDate;
  const cost = serviceModel.cost(service);
  const discountPeriodExpiration = cost.discount ? serviceCostModel.discountPeriodExpiration({
    discount: cost.discount
  }) : undefined;
  const initialState = useMemo(() => getInitialDiscountPeriodExpirationState({
    now,
    isApprovedRelationship: isRelationshipApproved,
    existingExpiration: discountPeriodExpiration
  }), [now, isRelationshipApproved, discountPeriodExpiration]);
  const [selectedDate, setSelectedDate] = useState(initialState.selectedDate);
  const [timeframeType, setTimeframeType] = useState(initialState.timeframeType);
  const [periodType, setPeriodType] = useState(initialState.periodType);
  const [amount, setAmount] = useState(initialState.amount);
  const [error, setError] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  if (!isAwaitingApproval(service.status)) {
    return null;
  }
  useEffect(() => {
    // reset state when discountPeriodExpiration is removed
    if (!discountPeriodExpiration || discountPeriodExpiration.isNone()) {
      const defaultState = getInitialDiscountPeriodExpirationState({
        now,
        isApprovedRelationship: isRelationshipApproved
      });
      setTimeframeType(defaultState.timeframeType);
      setPeriodType(defaultState.periodType);
      setAmount(defaultState.amount);
      setSelectedDate(defaultState.selectedDate);
    }
  }, [discountPeriodExpiration, now, isRelationshipApproved]);
  useOnMount(() => {
    if (discountPeriodExpiration && discountPeriodExpiration.type() !== 'none') {
      const type = discountPeriodExpiration.type();
      setTimeframeType(type);
      const date = discountPeriodExpiration.date();
      const period = discountPeriodExpiration.period();
      if (type === 'date' && date) {
        setSelectedDate(date);
      } else if (type === 'period' && period) {
        var _discountPeriodExpira;
        setPeriodType(period);
        setAmount((_discountPeriodExpira = discountPeriodExpiration.amount()) !== null && _discountPeriodExpira !== void 0 ? _discountPeriodExpira : 1);
      }
    }
  });
  useEffect(() => {
    const error = validateDiscountPeriodExpiration(service, now, effectiveDate);
    setError(error);
    if (!service.cost.discount) {
      const defaultState = getInitialDiscountPeriodExpirationState({
        now,
        isApprovedRelationship: isRelationshipApproved
      });
      setTimeframeType(defaultState.timeframeType);
      setPeriodType(defaultState.periodType);
      setAmount(defaultState.amount);
      setSelectedDate(defaultState.selectedDate);
    }
  }, [service, now, effectiveDate, isRelationshipApproved]);
  const {
    minDate,
    maxDate
  } = useMemo(() => getDateRange(now, effectiveDate), [now, effectiveDate]);
  const amountOptions = useMemo(() => getAmountOptions(periodType), [periodType]);
  const isAmountValid = amountOptions.includes(amount);
  const handleChange = enabled => {
    onToggle(enabled);
  };
  const handleTimeframeTypeChange = newType => {
    if (onDiscountPeriodExpirationChange) {
      const expiration = newType === 'date' ? DiscountPeriodExpirationModel.makeDate(selectedDate) : DiscountPeriodExpirationModel.makePeriod(periodType, amount);
      onDiscountPeriodExpirationChange(service.id, expiration);
    }
    setTimeframeType(newType);
  };
  const handleDateChange = newDate => {
    if (onDiscountPeriodExpirationChange) {
      const expiration = DiscountPeriodExpirationModel.makeDate(newDate);
      onDiscountPeriodExpirationChange(service.id, expiration);
    }
    setSelectedDate(newDate);
  };
  const handleAmountChange = newAmount => {
    if (onDiscountPeriodExpirationChange && timeframeType === 'period') {
      const expiration = DiscountPeriodExpirationModel.makePeriod(periodType, newAmount);
      onDiscountPeriodExpirationChange(service.id, expiration);
    }
    setAmount(newAmount);
  };
  const handlePeriodTypeChange = newPeriodType => {
    if (onDiscountPeriodExpirationChange && timeframeType === 'period') {
      const expiration = DiscountPeriodExpirationModel.makePeriod(newPeriodType, amount);
      onDiscountPeriodExpirationChange(service.id, expiration);
    }
    setPeriodType(newPeriodType);
  };
  const shouldShowDefaultView = disabled || (discountPeriodExpiration === null || discountPeriodExpiration === void 0 ? void 0 : discountPeriodExpiration.isNone()) || !discountPeriodExpiration;
  const adjustedMinDate = useMemo(() => {
    return selectedDate < minDate ? selectedDate : minDate;
  }, [selectedDate, minDate]);
  return /*#__PURE__*/_jsx(AdvancedSettingsControl, {
    info: shouldShowDefaultView ? info : '',
    infoTestId: "".concat(testPrefix, "-discount-period-info"),
    disabled: disabled,
    label: shouldShowDefaultView ? label : '',
    isReadOnly: isReadOnly,
    testid: "".concat(testPrefix, "-discount-period"),
    disabledTooltip: shouldShowDefaultView ? disabledTooltip : '',
    onChange: handleChange,
    checked: !disabled && !!(discountPeriodExpiration !== null && discountPeriodExpiration !== void 0 && discountPeriodExpiration.hasExpiration()),
    labelSuffix: !shouldShowDefaultView && /*#__PURE__*/_jsx(WithInfoIcon, {
      className: styles.rowAndIconContainer,
      info: info,
      infoTestId: "".concat(testPrefix, "-discount-period-info"),
      children: /*#__PURE__*/_jsxs("div", {
        className: styles.rowContent,
        children: [/*#__PURE__*/_jsx(Typography, {
          children: isRelationshipApproved ? 'Expire discount on' : 'Expire discount'
        }), timeframeType === 'date' ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [!isRelationshipApproved && /*#__PURE__*/_jsxs(InlineSelect, {
            skin: "primary",
            disabled: disabled,
            value: timeframeType,
            testid: "".concat(testPrefix, "-timeframe-type"),
            onChange: e => handleTimeframeTypeChange(e.target.value),
            SelectProps: {
              renderValue: () => 'on'
            },
            children: [/*#__PURE__*/_jsx(MenuItem, {
              value: "date",
              children: "On a specific date"
            }), /*#__PURE__*/_jsx(MenuItem, {
              value: "period",
              children: "A set time after the agreement effective date"
            })]
          }), /*#__PURE__*/_jsx(Tooltip, {
            title: isCalendarOpen ? '' : error,
            placement: "top",
            children: /*#__PURE__*/_jsx("div", {
              style: {
                position: 'relative'
              },
              onFocus: () => setIsCalendarOpen(true),
              onBlur: () => setIsCalendarOpen(false),
              children: /*#__PURE__*/_jsx(KeyboardDatePicker, {
                style: {
                  width: 160,
                  textAlign: 'center'
                },
                size: "small",
                "data-testid": "".concat(testPrefix, "-deadline-date"),
                value: selectedDate,
                minDate: adjustedMinDate,
                maxDate: maxDate,
                disabled: disabled,
                disableErrors: !error,
                helperText: "",
                error: !!error,
                onChange: newDate => {
                  if (newDate && newDate < minDate) {
                    handleDateChange(minDate);
                  } else if (newDate) {
                    handleDateChange(newDate);
                  }
                },
                shouldDisableDate: date => !!date && (date < minDate || date > maxDate) && !isSameDay(date, selectedDate)
              })
            })
          })]
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Tooltip, {
            title: error,
            placement: "top",
            disableArrow: true,
            "data-testid": "".concat(testPrefix, "-amount-select-tooltip"),
            children: /*#__PURE__*/_jsxs(InlineSelect, {
              skin: "primary",
              disabled: disabled,
              value: amount.toString(),
              testid: "".concat(testPrefix, "-amount-select"),
              onChange: e => handleAmountChange(Number(e.target.value)),
              className: styles.amountSelect,
              error: !isAmountValid,
              children: [amountOptions.map(value => /*#__PURE__*/_jsx(MenuItem, {
                value: value.toString(),
                "data-testid": "".concat(testPrefix, "-amount-option-").concat(value),
                children: value
              }, value)), !isAmountValid && /*#__PURE__*/_jsx(MenuItem, {
                value: amount.toString(),
                disabled: true,
                style: {
                  display: 'none'
                },
                children: amount
              })]
            })
          }), /*#__PURE__*/_jsx(InlineSelect, {
            skin: "primary",
            disabled: disabled,
            value: periodType,
            testid: "".concat(testPrefix, "-period-select"),
            onChange: e => handlePeriodTypeChange(e.target.value),
            children: PERIOD_OPTIONS.map(period => /*#__PURE__*/_jsx(MenuItem, {
              value: period,
              "data-testid": "".concat(testPrefix, "-period-type-option-").concat(period),
              children: pluralize(period, amount)
            }, period))
          }), /*#__PURE__*/_jsxs(InlineSelect, {
            skin: "primary",
            disabled: disabled,
            value: timeframeType,
            testid: "".concat(testPrefix, "-timeframe-type"),
            onChange: e => handleTimeframeTypeChange(e.target.value),
            SelectProps: {
              renderValue: () => 'after the agreement effective date'
            },
            children: [/*#__PURE__*/_jsx(MenuItem, {
              value: "date",
              children: "On a specific date"
            }), /*#__PURE__*/_jsx(MenuItem, {
              value: "period",
              children: "A set time after the agreement effective date"
            })]
          })]
        })]
      })
    })
  }, AdvancedSetting.LimitedTimeDiscountOfferForAgreement);
}